"use client";

/**
 * ShareMediaKitButton - Client component for copying creator profile URL
 *
 * Behavior:
 * - Uses canonical URL (influencehub.net/u/[username]) via lib/urls/canonical
 * - COPY-ONLY: Copies URL to clipboard (no native share dialogs)
 * - Shows inline status message "Link copied" for ~2 seconds
 * - Includes "Built with InfluenceHub" subtext (optional)
 */

import React, { useState, useEffect, useRef } from 'react';
import { canonicalUrl } from '../../lib/urls/canonical';

interface ShareMediaKitButtonProps {
  username: string;
  className?: string;
  variant?: 'primary' | 'secondary' | 'success';
  size?: 'sm' | 'md';
  showSubtext?: boolean;
  label?: string;
}

type StatusType = 'idle' | 'success' | 'error';

export default function ShareMediaKitButton({ 
  username, 
  className = '',
  variant = 'primary',
  size = 'md',
  showSubtext = true,
  label = 'Share my media kit',
}: ShareMediaKitButtonProps) {
  const [status, setStatus] = useState<StatusType>('idle');
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [helperMessage, setHelperMessage] = useState<string>('');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Share link: canonical profile path /u/[username]
  const shareUrl = canonicalUrl(`u/${username}`);

  // Clear status after timeout (3000ms for success, 2000ms for error)
  useEffect(() => {
    if (status !== 'idle') {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      const timeoutDuration = status === 'success' ? 3000 : 2000;
      timeoutRef.current = setTimeout(() => {
        setStatus('idle');
        setStatusMessage('');
        setHelperMessage('');
      }, timeoutDuration);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [status]);

  const handleCopy = async () => {
    // Prevent rapid clicks - disable button while copying
    if (status !== 'idle') return;

    try {
      // Primary method: Modern clipboard API
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(shareUrl);
          setStatus('success');
          setStatusMessage('');
          setHelperMessage(`Paste this link in an email or DM to brands! ${shareUrl}`);
          return;
        } catch (clipboardError) {
          console.warn('Clipboard API error:', clipboardError);
          // Fall through to execCommand fallback
        }
      }

      // Fallback: execCommand (older browsers, iOS Safari)
      const textarea = document.createElement('textarea');
      textarea.value = shareUrl;
      textarea.style.position = 'fixed';
      textarea.style.top = '0';
      textarea.style.left = '0';
      textarea.style.opacity = '0';
      textarea.style.pointerEvents = 'none';
      textarea.setAttribute('readonly', '');
      document.body.appendChild(textarea);
      
      // Select text - works better on iOS
      if (navigator.userAgent.match(/ipad|iphone/i)) {
        const range = document.createRange();
        range.selectNodeContents(textarea);
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
        textarea.setSelectionRange(0, 999999);
      } else {
        textarea.select();
      }
      
      try {
        const success = document.execCommand('copy');
        document.body.removeChild(textarea);
        
        if (success) {
          setStatus('success');
          setStatusMessage('');
          setHelperMessage(`Paste this link in an email or DM to brands! ${shareUrl}`);
        } else {
          setStatus('error');
          setStatusMessage("Couldn't copy link");
          setHelperMessage('');
        }
      } catch (execError) {
        document.body.removeChild(textarea);
        console.error('execCommand copy error:', execError);
        setStatus('error');
        setStatusMessage("Couldn't copy link");
        setHelperMessage('');
      }
    } catch (error) {
      console.error('Copy error:', error);
      setStatus('error');
      setStatusMessage("Couldn't copy link");
      setHelperMessage('');
    }
  };

  // Button styles based on variant and size
  const buttonBaseStyles = 'inline-flex items-center justify-center font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
  
  const variantStyles = {
    primary: 'rounded-full bg-blue-600 text-white shadow-sm hover:bg-blue-700 focus:ring-blue-500',
    secondary: 'rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 focus:ring-slate-500',
    success: 'rounded-md bg-green-500 text-white shadow-sm hover:bg-green-600 focus:ring-2 focus:ring-green-400 focus:ring-offset-2',
  };
  
  const sizeStyles = {
    sm: 'px-3 py-1.5 text-xs sm:text-sm',
    md: 'px-4 py-2 text-sm',
  };
  
  const buttonClassName = `${buttonBaseStyles} ${variantStyles[variant]} ${sizeStyles[size]}`;
  
  // Container styles - adjust based on variant
  // For secondary and success variants, use inline-flex to work better in button groups
  const containerClassName = variant === 'secondary' || variant === 'success'
    ? `inline-flex flex-col gap-1 ${className}` 
    : `flex flex-col items-center sm:items-end gap-2 ${className}`;

  // Determine button label based on status
  const buttonLabel = status === 'success' ? 'Copied ✓' : label;

  return (
    <div className={containerClassName}>
      <button
        type="button"
        onClick={handleCopy}
        disabled={status !== 'idle'}
        className={`${buttonClassName} cursor-pointer`}
      >
        {buttonLabel}
      </button>
      
      {/* Helper message - shown when copied successfully */}
      {status === 'success' && helperMessage && (
        <p className="text-xs text-slate-500 text-center sm:text-left break-words">
          {helperMessage}
        </p>
      )}
      
      {/* Error message - shown when copy fails */}
      {status === 'error' && statusMessage && (
        <p className="text-xs text-red-600 text-center sm:text-left">
          {statusMessage}
        </p>
      )}
      
      {/* "Built with InfluenceHub" subtext - only show if showSubtext is true and not in success state */}
      {showSubtext && status !== 'success' && (
        <p className="text-[10px] text-slate-500 text-center sm:text-right">
          Built with InfluenceHub
        </p>
      )}
    </div>
  );
}

